/**
 * Reglas de precio de la matriz "por etapa": cómo se resuelve el precio
 * unitario, el coeficiente y la cantidad de cada celda, y cómo se combinan en lo
 * que efectivamente se liquida en esa visita.
 *
 * Viven fuera del componente porque son la definición del cálculo y no de la
 * pantalla: acá se pueden leer y probar sin montar la grilla.
 */
import { ContractConcept } from '../../../../models/Entities/Contracts/ContractConcept';
import { ContractStage } from '../../../../models/Entities/Contracts/ContractStage';
import { NewContractConceptPaymentSchedule } from '../../../../models/Entities/Contracts/NewContractConceptPaymentSchedule';

import { ConceptValuationOptionsEnum, SettlementOptionsEnum, isGroupedInAnyStage } from './ConceptGroupedView.Functions';

// ──────────────────────────────────────────────────────────
// Execution columns used in ByStage matrix
// ──────────────────────────────────────────────────────────
export type ExecutionKey = 'inFace' | 'remote' | 'phone';
export interface ExecutionDef {
    key: ExecutionKey;
    short: string;
    label: string;
}
export const allExecutions: ExecutionDef[] = [
    { key: 'inFace', short: 'Pres.', label: 'Presencial' },
    { key: 'remote', short: 'Rem.', label: 'Remoto' },
    { key: 'phone', short: 'Tel.', label: 'Telefónico' },
];

export const conceptApplies = (cc: ContractConcept, ex: ExecutionKey): boolean => {
    if (ex === 'inFace') return !!cc.applyToInFaceExecution;
    if (ex === 'remote') return !!cc.applyToRemoteExecution;
    return !!cc.applyToPhoneExecution;
};

export const getStageScheduleFor = (cc: ContractConcept, stageId: number): NewContractConceptPaymentSchedule | undefined => {
    return cc.paymentSchedule?.find(ps => (ps as any).stage?.id === stageId || ps.contractStageId === stageId);
};

export const getPriceForExecution = (sch: NewContractConceptPaymentSchedule | undefined, ex: ExecutionKey): number | undefined => {
    if (!sch) return undefined;
    if (ex === 'inFace') return sch.price;
    if (ex === 'remote') return sch.remotePrice;
    return sch.phonePrice;
};

export const getCoefficientForExecution = (sch: NewContractConceptPaymentSchedule | undefined, ex: ExecutionKey): number | undefined => {
    if (!sch) return undefined;
    if (ex === 'inFace') return sch.settlementCoefficient;
    if (ex === 'remote') return sch.settlementRemoteCoefficient;
    return sch.settlementPhoneCoefficient;
};

// Cantidad de la etapa. El editor siembra el borrador con `amountExpected || 1`
// (ContractStageMultiselector.Types), así que sobre una etapa ya programada un 0
// guardado significa "sin cargar", no "cero visitas": la cantidad neutra es 1.
// Sin fila de programación no hay cantidad, y devuelve 0 para no inflar la
// columna "Cantidad", que suma sobre todas las etapas de la grilla.
export const getQuantityForStage = (sch: NewContractConceptPaymentSchedule | undefined): number => {
    if (!sch) return 0;
    const quantity = sch.amountExpected;
    if (quantity === undefined || quantity === null || Number.isNaN(quantity) || quantity <= 0) return 1;
    return quantity;
};

// ──────────────────────────────────────────────────────────
// Concepts grouped within the visit (settled in any stage)
// ──────────────────────────────────────────────────────────
// They carry no per-stage schedule: the three fixed values on the concept itself
// are the per-modality prices, and they apply to every visit. ConceptForm labels
// them "Valor" / "Valor remoto" / "Valor telefónico".
export const getFixedValueForExecution = (cc: ContractConcept, ex: ExecutionKey): number | undefined => {
    if (ex === 'inFace') return cc.priceValue;
    if (ex === 'remote') return cc.remoteFixedValue;
    return cc.phoneFixedValue;
};

// True when the concept has to be priced from its own fixed values instead of a
// per-stage schedule row.
export const usesConceptFixedValues = (
    cc: ContractConcept,
    sch: NewContractConceptPaymentSchedule | undefined,
): boolean => !sch && isGroupedInAnyStage(cc);

// Los precios por etapa sólo se cargan cuando el concepto se valoriza por etapa:
// ConceptForm habilita esos campos con `ShowInputValues={valuationType === 5}`.
// Con cualquier otra valorización la fila de programación sólo lleva coeficiente
// y cantidad, y el precio unitario es el precio base del concepto.
export const pricesComeFromSchedule = (cc: ContractConcept): boolean =>
    cc.valuationType === ConceptValuationOptionsEnum.ValueByStage;

// Single price accessor for the grid: schedule row when the concept is priced
// per stage, the concept's own value otherwise.
export const getUnitPriceFor = (
    cc: ContractConcept,
    sch: NewContractConceptPaymentSchedule | undefined,
    ex: ExecutionKey,
): number | undefined => {
    if (usesConceptFixedValues(cc, sch)) return getFixedValueForExecution(cc, ex);
    if (pricesComeFromSchedule(cc)) return getPriceForExecution(sch, ex);
    return cc.priceValue;
};

// ──────────────────────────────────────────────────────────
// Stage modality → which execution column actually counts
// ──────────────────────────────────────────────────────────
// A stage is remote when on-site attendance is NOT mandatory.
// Mirrors Protocol.Activity.Functions (`isRemote: !stage.isOnSiteMandatory`).
export const isRemoteStage = (stage: ContractStage): boolean => !stage.isOnSiteMandatory;

export const isFixedValueByStage = (cc: ContractConcept): boolean =>
    cc.valuationType === ConceptValuationOptionsEnum.FixedValue
    && cc.settlementSetting === SettlementOptionsEnum.ByStage;

export const isMeaningfulValue = (value: number | undefined | null): boolean =>
    value !== undefined && value !== null && !Number.isNaN(value) && value !== 0;

/**
 * Si esta modalidad está configurada para la etapa. Lo que responde la pregunta
 * es el único dato que distingue una modalidad de otra: el precio por modalidad
 * cuando el concepto se valoriza por etapa, y el coeficiente por modalidad en
 * cualquier otro caso —ahí el precio unitario es el mismo para las tres—.
 */
export const hasExecutionValue = (
    cc: ContractConcept,
    sch: NewContractConceptPaymentSchedule | undefined,
    ex: ExecutionKey,
): boolean => {
    if (usesConceptFixedValues(cc, sch)) {
        return isMeaningfulValue(getFixedValueForExecution(cc, ex));
    }

    if (!sch) return false;

    return isMeaningfulValue(
        pricesComeFromSchedule(cc)
            ? getPriceForExecution(sch, ex)
            : getCoefficientForExecution(sch, ex)
    );
};

// Only ONE modality is settled per stage:
//   presencial (isRemote = false) → valor presencial
//   remoto     (isRemote = true)  → valor telefónico si existe, si no el remoto
// The remaining columns are informative only and never add up to a total.
export const getRelevantExecutionFor = (
    cc: ContractConcept,
    sch: NewContractConceptPaymentSchedule | undefined,
    stage: ContractStage,
): ExecutionKey => {
    if (!isRemoteStage(stage)) return 'inFace';
    return hasExecutionValue(cc, sch, 'phone') ? 'phone' : 'remote';
};

/**
 * Coeficiente que efectivamente multiplica en esta celda.
 *
 * Cuando el concepto se valoriza por coeficiente (valor fijo liquidado por
 * etapa) el coeficiente ES el valor: un 0 cargado a mano significa "esta
 * modalidad no aplica en esta etapa" y se respeta —es lo que la celda ya
 * dibuja con el icono de prohibido—.
 *
 * En el resto de los casos el coeficiente es un multiplicador sobre el precio,
 * y su valor neutro es 1: el editor siembra el borrador con `?? 1` / `|| 1`, así
 * que un 0 o un vacío en la fila guardada es "sin cargar" y no puede anular un
 * precio que sí está cargado.
 */
export const getEffectiveCoefficient = (
    cc: ContractConcept,
    sch: NewContractConceptPaymentSchedule | undefined,
    ex: ExecutionKey,
): number => {
    if (!sch) return 1;

    const coefficient = getCoefficientForExecution(sch, ex);

    if (isFixedValueByStage(cc)) {
        return coefficient ?? 0;
    }

    if (coefficient === undefined || coefficient === null || Number.isNaN(coefficient) || coefficient <= 0) {
        return 1;
    }

    return coefficient;
};

// Lo que se liquida en una celda: precio unitario × coeficiente × cantidad.
// Los tres factores salen del mismo lugar en el que se cargan (ConceptForm y su
// multiselector de etapas), y los tres cuentan: un concepto de 100 con
// coeficiente 4 y cantidad 2 liquida 800 en esa visita.
export const getExecutionTotalFor = (
    cc: ContractConcept,
    sch: NewContractConceptPaymentSchedule | undefined,
    ex: ExecutionKey,
): number => {
    // Grouped in the visit: the fixed value is charged once per visit, so it
    // counts once on every stage column.
    if (usesConceptFixedValues(cc, sch)) return getFixedValueForExecution(cc, ex) ?? 0;

    if (!sch) return 0;

    const unitPrice = getUnitPriceFor(cc, sch, ex) ?? 0;

    return unitPrice * getEffectiveCoefficient(cc, sch, ex) * getQuantityForStage(sch);
};

// Total for a concept on a stage = only the value of the stage modality.
export const getConceptTotalForStage = (
    cc: ContractConcept,
    sch: NewContractConceptPaymentSchedule | undefined,
    stage: ContractStage,
): number => {
    if (!sch && !usesConceptFixedValues(cc, sch)) return 0;
    return getExecutionTotalFor(cc, sch, getRelevantExecutionFor(cc, sch, stage));
};
